/**
 * apps/email/src/App.tsx
 * ----------------------
 * Standalone Email module shell at email.quotech.ai.
 *
 * Architecture (post-2026-05-13 SAM-restoration MR):
 *   @quotech/ui DashboardLayout              ← outer Bravada-style chrome
 *   ├── Header (logo, hub switcher, search,
 *   │           settings, version watcher,
 *   │           command palette, user menu)
 *   ├── Sidebar
 *   │   ├── MAIN_NAV_ITEMS = [Kanban]
 *   │   └── HUB_LINKS (cross-hub)
 *   └── Content
 *       └── @quotech/email-hub EmailLayout (default = standalone)
 *           ├── Inner sidebar
 *           │   ├── Compose CTA
 *           │   ├── Connect-mailbox card (when zero linked)
 *           │   ├── "Switch to Kanban" link
 *           │   ├── Inbox / Starred / Sent / Archived / Trash
 *           │   ├── Sync & Status / Email Settings / Tags & Rules
 *           │   ├── Mailbox switcher + Outlook footer
 *           │   ├── Capacity badge / Recent issues
 *           │   └── Collapse toggle
 *           └── Content
 *               ├── Outlook/Gmail status banners
 *               └── <Outlet /> (Inbox / Compose / Email view / etc.)
 *
 * Why this shape (the third revision in two days):
 *   - 2026-05-12 REC-0010 — Lego refit: flattened email-internal nav
 *     (Inbox/Starred/etc.) INTO DashboardLayout's sidebar. User
 *     reaction: "you instead put inbox etc in the left nav that is not
 *     where SAM's is... everything should be like the template."
 *   - 2026-05-13 (this MR): the SAM template puts those inside the
 *     email module's OWN inner sidebar (`packages/email-hub/.../
 *     EmailLayout.tsx`). The OUTER rail is for cross-module navigation
 *     only. Restored. Same nav shape SAM uses at /email today.
 *
 * Multi-tenant flow (unchanged):
 *   master JWT (cookie-scoped to .quotech.ai) → @quotech/auth resolves
 *   session → ApiAuthGate (from @quotech/email-hub) bridges access
 *   token + session into the package's axios → TenantBridge calls
 *   setEmailTenantId(orgId) on org switch → all email API calls
 *   include `?tenant=<orgId>` automatically.
 */

import { useEffect, useMemo, useState, useCallback, Suspense } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import {
  useAuth,
  ProtectedRoute,
  HubGuard,
  UnifiedSSOPage,
  SessionManager,
  useAuthStore,
  useComputedIsAdmin,
  useRBAC,
} from '@quotech/auth'
import { useApiClient } from '@quotech/api'
import {
  DashboardLayout,
  ErrorBoundary,
  VersionWatcher,
  buildSettingsTabs,
  useGlobalSearchBridge,
  useNavCustomization,
} from '@quotech/ui'
import {
  EmailLayout,
  InboxPage,
  EmailViewPage,
  StarredPage,
  SentPage,
  ArchivedPage,
  TrashPage,
  ComposePageRoute,
  SyncSettingsPage,
  ApiAuthGate,
  ToastProvider,
  // Compose-dock host — Reply / Reply-All / Forward / Compose push
  // windows into the shared dock store, rendered ONLY by <ComposeDock />
  // (a document.body portal). SAM/Newmark mount it in their AppShell;
  // the standalone email.quotech.ai SPA never did, so Reply did nothing
  // here too. Mounted inside EmailHubLayout (stable across /inbox,
  // /kanban) so an open draft survives navigation.
  ComposeDock,
  setEmailTenantId,
  setEmailBasePath,
  // Cross-org "Personal" workspace (FEAT-2026-06-22-CROSS-ORG-PERSONAL) —
  // peer routes (like Kanban / Drafting): the aggregate inbox + board
  // mirror render in the DashboardLayout content area, NOT inside
  // EmailLayout's single-org inner sidebar.
  PersonalInboxPage,
  PersonalBoardsPage,
  type AuthSession,
} from '@quotech/email-hub'
import {
  MODULE_CONFIG,
  MAIN_NAV_ITEMS,
  SECONDARY_NAV_ITEMS,
  SIDEBAR_SECTION_LABELS,
  HUB_LINKS,
  PAGE_TITLES,
} from './config/navigation'
import KanbanComingSoonPage from './pages/KanbanComingSoonPage'
import DraftingPage from './pages/DraftingPage'

// The email-hub package defaults its URL prefix to '/email' (correct
// for SAM where the feature mounts at /email/*). apps/email mounts
// at '/' so we override at module-load before any render so the inner
// sidebar's NavLink hrefs resolve under the apps/email root, not
// '/email/inbox' etc. setEmailBasePath updates a package singleton
// synchronously.
setEmailBasePath('')

/**
 * TenantBridge — keeps the email-hub package's `?tenant=N` query
 * param in sync with @quotech/auth's organisationId, so multi-org
 * users don't accidentally read another org's mail when they switch
 * organisations from the master dashboard's org picker.
 */
function TenantBridge({ children }: { children: React.ReactNode }) {
  const { organisationId } = useAuth()
  const numeric = organisationId ? Number.parseInt(organisationId, 10) : null
  const tenantId = Number.isFinite(numeric) ? numeric : null
  // BUG-2026-07-17-EMAIL-ORG-SWITCH-STALE-MAILBOX — publish during
  // render (parent-first) so the org-keyed remount below never lets a
  // freshly-mounted child fire its first fetch with the OLD tenant id
  // (effects run child-first). Module-singleton write; render-safe.
  setEmailTenantId(tenantId)
  // Re-key the subtree on org so every email surface (list state,
  // mailbox switcher, sync loops, SWR seeds) remounts cold against the
  // new organisation instead of tail-merging the previous org's rows.
  return <div key={tenantId ?? 'no-org'} className="contents">{children}</div>
}

/**
 * Maps @quotech/auth's User → email-hub's AuthSession shape. Both
 * IDs are strings on the auth side (master JWT is opaque about
 * numeric vs uuid) but the email-hub package's contract (lifted
 * verbatim from SAM's AuthUser/AuthOrganisation types) expects
 * numbers — backend `tenant=` query param and email row's
 * user_id/org_id columns are integers in the Django ORM.
 */
function buildSession(
  user: ReturnType<typeof useAuth>['user'],
  organisationId: string | null,
  organisationName: string | null,
): AuthSession {
  const userIdNum = user ? Number.parseInt(String(user.id), 10) : null
  const orgIdNum = organisationId ? Number.parseInt(organisationId, 10) : null
  return {
    user:
      user && Number.isFinite(userIdNum)
        ? {
            id: userIdNum as number,
            email: user.email,
            name: user.name,
            first_name: user.first_name,
            last_name: user.last_name,
          }
        : null,
    organisation: organisationId
      ? {
          id: Number.isFinite(orgIdNum) ? orgIdNum : null,
          name: organisationName ?? user?.organisation_name,
        }
      : null,
  }
}

function AuthBridge({ children }: { children: React.ReactNode }) {
  const { user, accessToken, organisationId, organisationName } = useAuth()
  const session = buildSession(user, organisationId, organisationName)
  return (
    <ApiAuthGate session={session} accessToken={accessToken}>
      <TenantBridge>{children}</TenantBridge>
    </ApiAuthGate>
  )
}

/**
 * Authenticated email module layout. Mirrors apps/qdrive/src/App.tsx
 * pattern: one component owning DashboardLayout config + nested
 * Routes for the email pages.
 */
function EmailHubLayout() {
  const location = useLocation()
  const { user, logout } = useAuth()
  const apiClient = useApiClient()
  const organisationId = useAuthStore((state) => state.organisationId)
  const [settingsOpen, setSettingsOpen] = useState(false)

  // Token + org provider for global search bridge — copies the
  // qdrive pattern verbatim. Without this the global Cmd+K search
  // bar in the top header can't authenticate against the master
  // backend.
  const tokenProvider = useMemo(
    () => ({
      getAccessToken: () => useAuthStore.getState().accessToken,
      // Mirrors qdrive's pattern. `selectedOrg` is set on the store
      // at runtime by the org-switcher in the master dashboard but
      // isn't yet declared in @quotech/auth's AuthState type. Cast
      // through unknown so the runtime read works without widening
      // the auth package's surface from here.
      getOrgId: () =>
        (useAuthStore.getState() as unknown as { selectedOrg?: { id?: string } }).selectedOrg?.id ||
        organisationId,
    }),
    [organisationId],
  )
  const { apiClient: searchApiClient } = useGlobalSearchBridge({
    tokenProvider,
    onSettingsOpen: useCallback(() => setSettingsOpen(true), []),
  })

  const pageTitle = useMemo(() => {
    const base = '/' + location.pathname.split('/')[1]
    return PAGE_TITLES[base] || PAGE_TITLES[location.pathname] || 'Emails'
  }, [location.pathname])

  const breadcrumbs = useMemo(() => {
    const items: Array<{ label: string; href?: string }> = [
      { label: 'Emails', href: '/' },
    ]
    if (pageTitle !== 'Emails') items.push({ label: pageTitle })
    return items
  }, [pageTitle])

  const handleLogout = useCallback(() => {
    logout()
    window.location.href = 'https://dashboard.quotech.ai'
  }, [logout])

  const userIsAdmin = useComputedIsAdmin(user as Record<string, unknown> | null)
  const { accessibleModules, permissions: rbacPermissions, hierarchyLevel } = useRBAC()

  const settingsTabs = useMemo(
    () =>
      buildSettingsTabs({
        user: user
          ? {
              id: user.id,
              first_name: user.name?.split(' ')[0],
              last_name: user.name?.split(' ').slice(1).join(' '),
              email: user.email,
              organisation_name: user.organisation_name,
              role: user.role,
              isAdmin: userIsAdmin,
              permissions: rbacPermissions,
              hierarchy_level: hierarchyLevel,
            }
          : null,
        hubId: 'email',
        hubName: 'Emails',
        apiClient,
        organisationId: organisationId || undefined,
        profileData: {
          first_name: user?.name?.split(' ')[0],
          last_name: user?.name?.split(' ').slice(1).join(' '),
          email: user?.email,
          organisation_name: user?.organisation_name,
        },
        organisationData: {
          id: organisationId || '',
          name: user?.organisation_name || 'Organisation',
        },
        // Outlook/Gmail OAuth lives in EmailLayout's inner sidebar
        // footer (the standalone presentation users actually click
        // through). Advertising the integrations here gives a second
        // discovery surface for users who land on Settings →
        // Integrations before /inbox. IntegrationStatus contract
        // lives in packages/ui/src/components/settings/
        // integrations-tab/integrations-tab-types.ts.
        integrations: [
          {
            id: 'outlook',
            name: 'Microsoft Outlook',
            description: 'Email + calendar sync via Microsoft Graph',
            isConnected: false,
            status: 'disconnected',
            isOAuth: true,
          },
          {
            id: 'gmail',
            name: 'Gmail',
            description: 'Email sync via Google Gmail API',
            isConnected: false,
            status: 'disconnected',
            isOAuth: true,
          },
        ],
        shortcuts: [
          { label: 'Compose', keys: ['C'], category: 'Email' },
          { label: 'Sync now', keys: ['G', 'S'], category: 'Email' },
          { label: 'Search', keys: ['⌘', 'F'], category: 'Email' },
        ],
      }),
    [user, apiClient, organisationId, userIsAdmin, rbacPermissions, hierarchyLevel],
  )

  // Right-click nav customisation — pin/unpin/move Inbox/Kanban/etc.
  // Same hook the master shell uses; the email hub gets its own
  // independent ordering keyed off `hubId: 'email'`. apiClient is
  // omitted (the email hub's apiClient is scoped to email-hub axios
  // not the master settings endpoint); state lives in localStorage,
  // which is fine — the master shell's NavigationTab modal already
  // syncs the same key across devices for users who log in to
  // dashboard.quotech.ai.
  const {
    pinnedItems,
    resolveNavContext,
    orderedNavItems,
    trackNavClick,
  } = useNavCustomization({
    hubId: 'email',
    navItems: MAIN_NAV_ITEMS,
    onOpenCustomize: () => setSettingsOpen(true),
  })

  // Header user carries the authority display email. Hoisted to a variable so
  // the explicit `displayEmail` key isn't rejected by object-literal
  // excess-property checks against the shared UI User prop. Login identity
  // (JWT / account / sender) stays on `email`.
  const headerUser = user ? { ...user, isAdmin: userIsAdmin, displayEmail: user.display_email || user.authority_email || user.email } : undefined

  return (
    <DashboardLayout
      module={MODULE_CONFIG}
      currentHub="email"
      accessibleModules={accessibleModules}
      navItems={orderedNavItems}
      secondaryNavItems={SECONDARY_NAV_ITEMS}
      externalLinks={HUB_LINKS}
      pinnedItems={pinnedItems}
      resolveNavContext={resolveNavContext}
      onNavItemClick={(item) => trackNavClick(item.id)}
      // Smart-default collapse: EmailLayout owns its own rich inner
      // rail (Inbox/Starred/Sent/...), so the outer @quotech/ui
      // rail competes for screen space. We collapse it by default
      // for fresh users; the first chevron toggle flips the
      // touched sentinel and the user's choice wins forever after.
      // See packages/ui/src/hooks/useSidebar.ts for semantics.
      sidebarHubId="email"
      sidebarDefaultCollapsed
      sidebarSectionLabels={SIDEBAR_SECTION_LABELS}
      breadcrumbs={breadcrumbs}
      showHubSwitcher
      showGlobalSearch
      globalSearchApiClient={searchApiClient}
      showCommandPalette
      hubLinks={HUB_LINKS}
      user={headerUser}
      onLogout={handleLogout}
      contentClassName="!p-0"
      showSettingsModal
      settingsModalOpen={settingsOpen}
      onOpenSettings={() => setSettingsOpen(true)}
      onCloseSettings={() => setSettingsOpen(false)}
      settingsTabs={settingsTabs}
    >
      <Suspense
        fallback={
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="w-10 h-10 border-4 border-rose-600 border-t-transparent rounded-full animate-spin" />
          </div>
        }
      >
        <Routes>
          {/* Kanban placeholder — peer left-nav module. See
              ./pages/KanbanComingSoonPage for the follow-up extraction
              plan. Keeps the inner email sidebar's "Switch to Kanban"
              button (packages/email-hub EmailLayout ~line 1020) from
              404'ing while the kanban-hub package is being lifted out
              of apps/sam. */}
          <Route path="/kanban" element={<KanbanComingSoonPage />} />

          {/* Drafting Studio — IR-driven drawing canvas. Peer route (like
              Kanban): renders in the DashboardLayout content area, NOT
              inside EmailLayout's inner email sidebar. P1 = manual draw +
              markup + export; AI generation lands in P3. */}
          <Route path="/drafting" element={<DraftingPage />} />

          {/* Cross-org "Personal" workspace — peer routes (like Kanban /
              Drafting): render in the DashboardLayout content area, NOT
              inside EmailLayout's single-org inner sidebar. The aggregate
              inbox spans every org the operator belongs to; the board
              mirror overlays their private cross-tag pins. See
              FEAT-2026-06-22-CROSS-ORG-PERSONAL. */}
          <Route path="/personal" element={<PersonalInboxPage />} />
          <Route path="/personal/boards" element={<PersonalBoardsPage />} />

          {/* Email pages — render INSIDE the standalone SAM-style
              EmailLayout. Default `presentation` is 'standalone' so
              the inner sidebar (Compose / Inbox / Starred / Sent /
              Archived / Trash / Sync & Status / Email Settings /
              Tags & Rules / mailbox switcher / Outlook footer /
              Recent issues) renders verbatim — same chrome SAM ships
              at /email today. */}
          <Route path="/" element={<EmailLayout />}>
            <Route index element={<Navigate to="/inbox" replace />} />
            <Route path="inbox" element={<InboxPage />} />
            <Route path="starred" element={<StarredPage />} />
            <Route path="sent" element={<SentPage />} />
            <Route path="archived" element={<ArchivedPage />} />
            <Route path="trash" element={<TrashPage />} />
            <Route path="compose" element={<ComposePageRoute />} />
            <Route path="settings" element={<SyncSettingsPage />} />
            <Route path=":id" element={<EmailViewPage />} />
            <Route path="*" element={<Navigate to="/inbox" replace />} />
          </Route>
        </Routes>
      </Suspense>
      {/* Floating compose-dock host — see import note. */}
      <ComposeDock />
    </DashboardLayout>
  )
}

function App() {
  // Master session monitor — refreshes JWT, propagates expiry to
  // useAuthStore, syncs integration health. Same pattern apps/sam
  // and apps/qdrive use; runs once for the lifetime of the SPA.
  useEffect(() => {
    const sessionManager = new SessionManager()
    sessionManager.startMonitoring()
    return () => sessionManager.stopMonitoring()
  }, [])

  return (
    <ErrorBoundary>
      <VersionWatcher pollIntervalMs={30_000} autoReloadAfterMs={null} />
      <ToastProvider>
        <Routes>
          <Route
            path="/sso"
            element={
              <UnifiedSSOPage
                defaultRedirectPath="/inbox"
                loadingComponent={
                  <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
                    <div className="text-center">
                      <div className="w-10 h-10 border-4 border-rose-600 border-t-transparent rounded-full animate-spin mx-auto" />
                      <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
                        Signing you in to Emails…
                      </p>
                    </div>
                  </div>
                }
              />
            }
          />
          <Route
            path="/*"
            element={
              <ProtectedRoute redirectTo="https://dashboard.quotech.ai/login">
                <HubGuard hub="email">
                  <AuthBridge>
                    <EmailHubLayout />
                  </AuthBridge>
                </HubGuard>
              </ProtectedRoute>
            }
          />
        </Routes>
      </ToastProvider>
    </ErrorBoundary>
  )
}

export default App
